/**
 * Portal Documents Tab Component
 *
 * Displays attached reference documents in the title/escrow portal.
 * Provides download functionality for each document.
 */

import { useState } from 'react';
import {
  FileText,
  Download,
  Loader2,
  AlertCircle,
  FolderOpen,
  Upload,
  CheckCircle2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { PortalDocument } from '@/types/conditions-portal';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('PortalDocumentsTab', 'conditions-portal');

// API configuration
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

interface PortalDocumentsTabProps {
  documents: PortalDocument[];
  token: string;
}

/**
 * Format file size for display
 */
function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes) {
return '';
}
  if (bytes < 1024) {
return `${bytes} B`;
}
  if (bytes < 1024 * 1024) {
return `${(bytes / 1024).toFixed(0)} KB`;
}
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Format date for display
 */
function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) {
return '';
}
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

/**
 * Get file icon based on MIME type
 */
function getFileIcon(mimeType: string | null | undefined) {
  if (!mimeType) {
return FileText;
}
  if (mimeType.includes('pdf')) {
return FileText;
}
  if (mimeType.includes('image')) {
return FileText;
}
  return FileText;
}

interface DocumentItemProps {
  document: PortalDocument;
  token: string;
  onDownload: (doc: PortalDocument) => Promise<void>;
  downloading: boolean;
}

function DocumentItem({ document, token, onDownload, downloading }: DocumentItemProps) {
  const FileIcon = getFileIcon(document.mimeType);
  const isEncompass = document.source === 'encompass';

  return (
    <div
      className={cn(
        'flex items-center gap-4 p-4 rounded-lg border transition-colors',
        'hover:bg-muted/50',
        downloading && 'opacity-70'
      )}
    >
      {/* File Icon */}
      <div
        className={cn(
          'h-12 w-12 rounded-lg flex items-center justify-center',
          isEncompass ? 'bg-amber-100' : 'bg-blue-100'
        )}
      >
        {isEncompass ? (
          <FolderOpen className="h-6 w-6 text-amber-600" />
        ) : (
          <Upload className="h-6 w-6 text-blue-600" />
        )}
      </div>

      {/* File Info */}
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">{document.displayName}</p>
        <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
          {document.fileSize && (
            <span>{formatFileSize(document.fileSize)}</span>
          )}
          {document.attachedAt && (
            <span>Added {formatDate(document.attachedAt)}</span>
          )}
          <Badge
            variant="secondary"
            className={cn(
              'text-xs',
              isEncompass
                ? 'bg-amber-100 text-amber-700 hover:bg-amber-100'
                : 'bg-blue-100 text-blue-700 hover:bg-blue-100'
            )}
          >
            {isEncompass ? 'Encompass' : 'Uploaded'}
          </Badge>
        </div>
      </div>

      {/* Download Button */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => onDownload(document)}
        disabled={downloading}
        className="shrink-0"
      >
        {downloading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <>
            <Download className="h-4 w-4 mr-2" />
            Download
          </>
        )}
      </Button>
    </div>
  );
}

export function PortalDocumentsTab({ documents, token }: PortalDocumentsTabProps) {
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * Handle document download
   */
  const handleDownload = async (document: PortalDocument) => {
    setDownloadingId(document.id);
    setError(null);

    try {
      log.info('Downloading portal document', { documentId: document.id });

      // Call edge function to get download URL
      const response = await fetch(`${SUPABASE_URL}/functions/v1/conditions-portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: SUPABASE_ANON_KEY
        },
        body: JSON.stringify({
          action: 'get-portal-document',
          token,
          documentId: document.id
        })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to get download URL');
      }

      // Trigger download
      const link = window.document.createElement('a');
      link.href = result.downloadUrl;
      link.download = document.displayName;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);

      log.info('Document download initiated', { documentId: document.id });
    } catch (err) {
      log.error('Document download failed', { documentId: document.id, error: err });
      setError(err instanceof Error ? err.message : 'Failed to download document');
    } finally {
      setDownloadingId(null);
    }
  };

  // Empty state
  if (documents.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-lg font-medium text-muted-foreground">No Documents</p>
          <p className="text-sm text-muted-foreground mt-1">
            No reference documents have been attached to this portal.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Group by source
  const encompassDocs = documents.filter(d => d.source === 'encompass');
  const uploadedDocs = documents.filter(d => d.source === 'upload');

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold flex items-center gap-2">
          <FileText className="h-5 w-5 text-[#183C2E]" />
          Reference Documents ({documents.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Success indicator */}
        <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
          <div>
            <p className="text-sm font-medium text-green-800">
              Documents Ready for Download
            </p>
            <p className="text-sm text-green-700">
              These documents have been shared with you by the lender for your reference.
            </p>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="h-5 w-5 text-red-600 shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Encompass documents */}
        {encompassDocs.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
              <FolderOpen className="h-4 w-4" />
              From Loan File ({encompassDocs.length})
            </h4>
            <div className="space-y-2">
              {encompassDocs.map(doc => (
                <DocumentItem
                  key={doc.id}
                  document={doc}
                  token={token}
                  onDownload={handleDownload}
                  downloading={downloadingId === doc.id}
                />
              ))}
            </div>
          </div>
        )}

        {/* Uploaded documents */}
        {uploadedDocs.length > 0 && (
          <div>
            {encompassDocs.length > 0 && <div className="border-t my-4" />}
            <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
              <Upload className="h-4 w-4" />
              Additional Documents ({uploadedDocs.length})
            </h4>
            <div className="space-y-2">
              {uploadedDocs.map(doc => (
                <DocumentItem
                  key={doc.id}
                  document={doc}
                  token={token}
                  onDownload={handleDownload}
                  downloading={downloadingId === doc.id}
                />
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default PortalDocumentsTab;
